/* Materials — the Fleet Dispatch product catalogue (fleet analogue of Mix Designs).
   Add/edit/delete the materials a yard delivers; these feed the job order Product dropdown. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Boxes } from "lucide-react";
import { api } from "@/lib/api";
import { FLEET_UNITS, unitLabel } from "@/lib/industry";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const inputCls = "w-full mt-1 rounded-none bg-transparent border border-industrial mono text-sm p-2";
const EMPTY = { name: "", unit: "m3", price_per_unit: "", active: true };

export default function Materials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/materials"); setItems(data || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load materials"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setF(EMPTY); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setF({ name: m.name, unit: m.unit || "m3", price_per_unit: m.price_per_unit ?? "", active: m.active !== false }); setOpen(true); };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Give the material a name");
    setBusy(true);
    try {
      const payload = { name: f.name.trim(), unit: f.unit, active: f.active, price_per_unit: f.price_per_unit === "" ? null : Number(f.price_per_unit) };
      if (editing) { await api.put(`/materials/${editing.id}`, payload); toast.success("Material updated"); }
      else { await api.post("/materials", payload); toast.success("Material added"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the material"); }
    finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.name}"? Jobs already using it keep their product name.`)) return;
    try { await api.delete(`/materials/${m.id}`); toast.success("Material deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto" data-testid="materials-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-industrial flex items-center gap-2"><Boxes className="w-3.5 h-3.5" /> Fleet Dispatch</div>
          <h1 className="heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-ink mt-1">Materials</h1>
          <p className="text-sm text-muted-industrial mt-1">The products your yard delivers. These fill the Product list on every job order.</p>
        </div>
        <button type="button" onClick={openNew} data-testid="material-new-btn"
          className="shrink-0 inline-flex items-center gap-2 bg-primary-orange text-black font-black uppercase tracking-widest text-xs px-4 py-3 hover:opacity-90">
          <Plus className="w-4 h-4" /> Add material
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 mono text-xs uppercase tracking-widest text-muted-industrial py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-industrial p-12 text-center" data-testid="materials-empty">
          <Boxes className="w-8 h-8 mx-auto text-muted-industrial mb-3" />
          <p className="mono text-xs uppercase tracking-widest text-muted-industrial">No materials yet</p>
          <button type="button" onClick={openNew} className="mt-4 inline-flex items-center gap-2 border border-industrial px-4 py-2 mono text-[10px] uppercase tracking-widest hover:border-primary-orange hover:text-primary-orange">
            <Plus className="w-3.5 h-3.5" /> Add your first material
          </button>
        </div>
      ) : (
        <div className="border border-industrial divide-y divide-industrial" data-testid="materials-list">
          {items.map((m) => (
            <div key={m.id} className="flex items-center gap-4 p-4 hover:bg-surface-2" data-testid={`material-row-${m.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-bold uppercase text-ink truncate">{m.name}</div>
                <div className="mono text-[11px] text-muted-industrial mt-0.5">
                  Sold by {unitLabel(m.unit)}{m.price_per_unit != null ? ` · $${Number(m.price_per_unit).toFixed(2)}/${unitLabel(m.unit)}` : " · no price set"}
                  {m.active === false && <span className="text-red-500"> · inactive</span>}
                </div>
              </div>
              <button type="button" onClick={() => openEdit(m)} data-testid={`material-edit-${m.id}`} className="p-2 text-muted-industrial hover:text-primary-orange"><Pencil className="w-4 h-4" /></button>
              <button type="button" onClick={() => remove(m)} data-testid={`material-delete-${m.id}`} className="p-2 text-muted-industrial hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial" data-testid="material-dialog">
          <DialogHeader><DialogTitle className="heading uppercase tracking-wide">{editing ? "Edit material" : "New material"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="block"><span className="label-tag">Name *</span>
              <input data-testid="material-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Pine bark mulch, 20mm blue metal" className={inputCls} autoFocus />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="label-tag">Sold by</span>
                <select data-testid="material-unit" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className={inputCls}>
                  {FLEET_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </label>
              <label className="block"><span className="label-tag">Price / unit (optional)</span>
                <input data-testid="material-price" type="number" step="0.01" min="0" value={f.price_per_unit} onChange={(e) => setF({ ...f, price_per_unit: e.target.value })} placeholder="e.g. 62.00" className={inputCls} />
              </label>
            </div>
            <label className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-muted-industrial">
              <input type="checkbox" data-testid="material-active" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active (show in job orders)
            </label>
          </div>
          <DialogFooter>
            <button type="button" onClick={save} disabled={busy} data-testid="material-save"
              className="bg-primary-orange text-black font-black uppercase tracking-widest text-xs px-5 py-3 disabled:opacity-50 inline-flex items-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Save changes" : "Add material"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
